import React from 'react';
import styled from '@emotion/styled';

interface ResponsiveProductImageProps {
  src: string;
  alt: string;
  className?: string;
  displayWidth?: {
    mobile: number;
    desktop: number;
  };
}

const StyledImg = styled.img`
  width: 100%;
  height: auto;
  object-fit: contain;
`;

/**
 * Generates responsive image URLs from BigCommerce CDN
 * BigCommerce format: .../image.png?c=1 or .../image.386.513.png?c=1
 */
const generateBigCommerceSrcSet = (
  baseUrl: string,
  sizes: number[],
): string => {
  // Parse the base URL
  const url = new URL(baseUrl);
  const pathname = url.pathname;

  // Remove existing size parameters (e.g., .386.513)
  const cleanPath = pathname.replace(/\.\d+\.\d+\.png/, '.png');

  return sizes
    .map((width) => {
      const newUrl = new URL(url);
      // BigCommerce sizing format: width x width for square products
      newUrl.pathname = cleanPath.replace('.png', `.${width}.${width}.png`);
      return `${newUrl.toString()} ${width}w`;
    })
    .join(', ');
};

/**
 * Optimized responsive image component for product images
 * Supports BigCommerce CDN automatic resizing
 */
export const ResponsiveProductImage = ({
  src,
  alt,
  className,
  displayWidth = { mobile: 240, desktop: 218 },
}: ResponsiveProductImageProps) => {
  const isBigCommerce = src.includes('bigcommerce.com');

  if (!isBigCommerce) {
    // Fallback for non-BigCommerce images
    return <StyledImg src={src} alt={alt} className={className} />;
  }

  // Generate responsive sizes
  // Use 1x, 2x, and 3x for retina displays
  const mobileSizes = [
    displayWidth.mobile,
    displayWidth.mobile * 2,
    displayWidth.mobile * 3,
  ];
  const desktopSizes = [
    displayWidth.desktop,
    displayWidth.desktop * 2,
    displayWidth.desktop * 3,
  ];

  // Combine and deduplicate sizes
  const allSizes = Array.from(new Set([...mobileSizes, ...desktopSizes])).sort(
    (a, b) => b - a,
  );

  const srcSet = generateBigCommerceSrcSet(src, allSizes);
  const sizes = `(min-width: 768px) ${displayWidth.desktop}px, ${displayWidth.mobile}px`;

  return (
    <StyledImg
      src={src}
      srcSet={srcSet}
      sizes={sizes}
      alt={alt}
      className={className}
      loading="lazy"
      decoding="async"
    />
  );
};
